<template>
  <div class="gv">
    <!-- Login ohmyart-ai -->
    <div v-if="!authUser && !initializing" class="gv__login">
      <div class="gv__loginCard">
        <div class="gv__loginTitle"><v-icon color="#7b5cd6" class="mr-2">mdi-auto-fix</v-icon>Generieren</div>
        <div class="gv__muted mb-4">Login für das Projekt ohmyart-ai, derselbe wie beim Generation Monitor.</div>
        <v-text-field v-model="loginEmail" label="E-Mail" outlined dense rounded hide-details class="mb-2" prepend-inner-icon="mdi-email-outline" />
        <v-text-field v-model="loginPassword" label="Passwort" type="password" outlined dense rounded hide-details prepend-inner-icon="mdi-lock-outline" @keyup.enter="handleLogin" />
        <div v-if="authError" class="gv__error mt-2">{{ authError }}</div>
        <v-btn color="primary" depressed rounded block class="mt-4" :loading="authLoading" @click="handleLogin">Anmelden</v-btn>
      </div>
    </div>

    <template v-if="authUser">
      <div class="gv__top">
        <div class="gv__title"><v-icon color="#7b5cd6" class="mr-2">mdi-auto-fix</v-icon><b>Generieren</b><span class="gv__muted">wie im Shop-Editor, ohne Tageslimit · {{ authUser.email }}</span></div>
        <v-spacer />
        <v-btn icon small title="Abmelden" @click="handleLogout"><v-icon small>mdi-logout</v-icon></v-btn>
      </div>

      <div class="gv__layout">
        <!-- Eingabe -->
        <div class="gv__panel">
          <!-- 1 Fotos -->
          <div class="gv__card">
            <div class="gv__cardHead"><span class="gv__step">1</span><b>Fotos</b><span class="gv__muted">1 Foto, oder 2 bis 4 zum Kombinieren</span></div>
            <div class="gv__drop" :class="{ 'gv__drop--over': dragOver }" @dragover.prevent="dragOver = true" @dragleave="dragOver = false" @drop.prevent="onDrop" @click="$refs.file.click()">
              <input ref="file" type="file" accept="image/*" multiple style="display: none" @change="onPick" />
              <template v-if="!uploads.length"><v-icon large color="rgba(0,0,0,.35)">mdi-image-plus</v-icon><div class="gv__muted">Klicken oder Fotos hierher ziehen</div></template>
              <div v-else class="gv__thumbs" @click.stop>
                <div v-for="u in uploads" :key="u.id" class="gv__thumb" :class="{ 'gv__thumb--busy': u.uploading, 'gv__thumb--err': u.error }">
                  <img :src="u.preview" alt="" />
                  <v-progress-circular v-if="u.uploading" indeterminate size="20" width="2" color="#7b5cd6" class="gv__thumbSpin" />
                  <button type="button" class="gv__thumbX" title="Entfernen" @click.stop="removeUpload(u.id)">×</button>
                </div>
                <button v-if="uploads.length < 4" type="button" class="gv__thumbAdd" @click.stop="$refs.file.click()"><v-icon>mdi-plus</v-icon></button>
              </div>
            </div>
            <div v-if="uploads.length >= 2" class="gv__hint"><v-icon x-small>mdi-image-multiple-outline</v-icon> {{ uploads.length }} Fotos werden zu einem Motiv kombiniert, nur v3-Stile.</div>
          </div>

          <!-- 2 Stil -->
          <div class="gv__card">
            <div class="gv__cardHead"><span class="gv__step">2</span><b>Stil</b><span v-if="metaError" class="gv__error">{{ metaError }}</span><span v-else-if="!styles.length" class="gv__muted">lädt …</span></div>
            <div class="gv__styles">
              <button v-for="s in styles" :key="s.id" type="button" class="gv__style" :class="{ 'gv__style--on': styleId === s.id }" @click="selectStyle(s.id)">
                <img :src="exampleUrl(s, orientation, versionFor(s))" alt="" loading="lazy" @error="onExampleError($event, s)" />
                <span class="gv__styleName">{{ s.id }}<span v-if="s.versions > 1" class="gv__styleV">v{{ versionFor(s) }}</span></span>
              </button>
            </div>
          </div>

          <!-- 3 Ausrichtung + Version -->
          <div class="gv__card">
            <div class="gv__cardHead"><span class="gv__step">3</span><b>Ausrichtung &amp; Version</b></div>
            <div class="gv__rowOpts">
              <div class="gv__segmented">
                <button type="button" class="gv__segBtn" :class="{ 'gv__segBtn--on': orientation === 'portrait' }" @click="orientation = 'portrait'"><v-icon small>mdi-crop-portrait</v-icon> Hochformat</button>
                <button type="button" class="gv__segBtn" :class="{ 'gv__segBtn--on': orientation === 'landscape' }" @click="orientation = 'landscape'"><v-icon small>mdi-crop-landscape</v-icon> Querformat</button>
              </div>
              <div v-if="currentStyle" class="gv__segmented">
                <button v-for="v in currentStyle.versions" :key="v" type="button" class="gv__segBtn" :class="{ 'gv__segBtn--on': version === v }" @click="version = v">V{{ v }}<span v-if="currentStyle.transparentVersions.includes(v)" class="gv__segTag">Freisteller</span></button>
              </div>
            </div>
            <div v-if="currentVersionMeta" class="gv__metaLine">
              <span class="gv__chip">Engine {{ currentVersionMeta.engine || "–" }}</span>
              <span class="gv__chip">Upscaler {{ currentVersionMeta.upscaler }}</span>
              <span v-if="currentVersionMeta.print" class="gv__chip">Druck: {{ currentVersionMeta.print.upscale && currentVersionMeta.print.upscale.engine }}<template v-if="currentVersionMeta.print.detailPass && currentVersionMeta.print.detailPass.enabled"> + Detail-Pass</template></span>
              <span v-if="currentVersionMeta.cutout" class="gv__chip">Freisteller {{ (currentVersionMeta.print && currentVersionMeta.print.cutout && currentVersionMeta.print.cutout.method) || "dual" }}</span>
            </div>
          </div>

          <v-btn color="primary" depressed rounded block large class="gv__go" :disabled="!canGenerate" :loading="generating" @click="generate">
            <v-icon left>mdi-auto-fix</v-icon>{{ generating ? `Generiert … ${elapsed}` : "Generieren" }}
          </v-btn>
          <div v-if="genError" class="gv__error mt-2">{{ genError }}</div>
          <div class="gv__muted mt-2">Kosten je Lauf wie im Shop, etwa 6 bis 8 Cent. Ergebnisse landen in artworks_generated und im Generation Monitor.</div>
        </div>

        <!-- Ergebnisse -->
        <div class="gv__results">
          <div v-if="!results.length" class="gv__empty"><v-icon large color="rgba(0,0,0,.25)">mdi-image-outline</v-icon><div class="gv__muted">Noch keine Ergebnisse in dieser Sitzung.</div></div>
          <div v-for="r in results" :key="r.id" class="gv__result">
            <div class="gv__resultHead">
              <b>{{ r.styleId }} v{{ r.version }}</b>
              <span class="gv__chip">{{ r.orientation === "landscape" ? "Quer" : "Hoch" }}</span>
              <span v-if="r.combined" class="gv__chip">{{ r.inputs.length }} Fotos</span>
              <span v-if="r.cutout && r.cutout !== 'none'" class="gv__chip" :class="{ 'gv__chip--bad': r.cutout === 'failed' }">Freisteller {{ r.cutout }}</span>
              <span class="gv__muted">{{ fmtTime(r.at) }} · {{ fmtMs(r.ms) }}</span>
              <v-spacer />
              <div v-if="r.opaqueUrl" class="gv__segmented gv__segmented--mini">
                <button type="button" class="gv__segBtn" :class="{ 'gv__segBtn--on': r.view !== 'opaque' }" @click="r.view = 'final'">freigestellt</button>
                <button type="button" class="gv__segBtn" :class="{ 'gv__segBtn--on': r.view === 'opaque' }" @click="r.view = 'opaque'">opak</button>
              </div>
              <div v-if="r.cutout === 'done'" class="gv__segmented gv__segmented--mini">
                <button type="button" class="gv__segBtn" :class="{ 'gv__segBtn--on': r.bg === 'dark' }" @click="r.bg = 'dark'">dunkel</button>
                <button type="button" class="gv__segBtn" :class="{ 'gv__segBtn--on': r.bg === 'light' }" @click="r.bg = 'light'">hell</button>
              </div>
              <button type="button" class="gv__iconBtn" title="Aus der Liste entfernen" @click="removeResult(r.id)"><v-icon small>mdi-close</v-icon></button>
            </div>
            <div class="gv__resultBody">
              <div class="gv__inputs"><img v-for="(u, i) in r.inputs" :key="i" :src="u" alt="" @click="openImage(u)" /></div>
              <div class="gv__out" :class="'gv__out--' + (r.bg || 'light')" @click="openImage(resultUrl(r))"><img :src="resultUrl(r)" alt="" /></div>
              <div class="gv__actions">
                <a class="gv__linkBtn" :href="r.url" target="_blank" rel="noopener"><v-icon small>mdi-download</v-icon> Artwork öffnen</a>
                <a v-if="r.opaqueUrl" class="gv__linkBtn" :href="r.opaqueUrl" target="_blank" rel="noopener"><v-icon small>mdi-download</v-icon> Opak öffnen</a>
                <button type="button" class="gv__linkBtn" @click="copy(r.url)"><v-icon small>mdi-content-copy</v-icon> URL kopieren</button>
                <span class="gv__muted gv__mono">doc {{ r.docId }}</span>

                <!-- Upscale -->
                <div v-if="r.meta && r.meta.print" class="gv__upscale">
                  <div class="gv__upHead"><b>Upscale</b><span class="gv__muted">Druckkette wie im Bestellweg, Quelle „lab", kein Task</span></div>
                  <div class="gv__upOpts">
                    <v-select v-model="r.up.engine" :items="engineItems(r)" dense outlined hide-details label="Engine" class="gv__select" />
                    <v-select v-model="r.up.resolution" :items="['4K', '2K']" dense outlined hide-details label="Auflösung" class="gv__select gv__select--s" />
                    <v-switch v-model="r.up.detailPass" dense hide-details label="Detail-Pass" class="gv__switch" />
                    <v-switch v-if="r.meta.transparent" v-model="r.up.cutout" dense hide-details label="Freistellen" class="gv__switch" />
                  </div>
                  <div class="gv__upRow">
                    <v-btn color="primary" depressed rounded small :disabled="!!r.up.jobId || r.up.starting" :loading="r.up.starting" @click="startUpscale(r)"><v-icon small left>mdi-arrow-expand-all</v-icon>Upscale starten</v-btn>
                    <span v-if="r.up.error" class="gv__error">{{ r.up.error }}</span>
                    <span v-else-if="r.up.jobId" class="gv__muted gv__mono">Job {{ r.up.jobId }}</span>
                    <button v-if="r.up.jobId" type="button" class="gv__linkBtn" @click="r.up.jobId = null; r.up.error = ''">neuer Lauf</button>
                  </div>
                  <UpscaleProgress v-if="r.up.jobId" :job-id="r.up.jobId" class="mt-2" />
                  <UpscaleJobResult v-if="r.up.jobId" :job-id="r.up.jobId" />
                </div>
                <div v-else-if="r.meta" class="gv__muted mt-2">Für diese Stil-Version gibt es kein Druckprofil, Upscale läuft hier nicht.</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <v-dialog v-model="imageDialog" max-width="92vw" content-class="gv__imgDialog">
        <div class="gv__imgWrap" @click="imageDialog = false"><img :src="activeImg" alt="" /></div>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from "firebase/auth";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { functions as mainFunctions } from "../../fb";
import { getOhmyartApp, styleExampleUrl } from "@/ohmyartApp";
import UpscaleProgress from "@/components/Upscales/UpscaleProgress.vue";
import { subscribeUpscaleJob } from "@/upscaleJobs";
import UpscaleJobCard from "@/components/Upscales/UpscaleJobCard.vue";
import { EventBus, events } from "../../event-bus";

const STORAGE_KEY = "taskdash_generate_results_v1";
const CLASSIC = ["original", "golden", "rainbow", "rustic", "light", "vector"];
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

/** Fertiger Upscale-Job: Karte erst zeigen, wenn das Job-Dokument da ist */
const UpscaleJobResult = {
  name: "UpscaleJobResult",
  components: { UpscaleJobCard },
  props: { jobId: { type: String, required: true } },
  data: () => ({ job: null, unsub: null }),
  watch: {
    jobId: { immediate: true, handler: "sub" },
  },
  beforeDestroy() {
    if (this.unsub) this.unsub();
  },
  methods: {
    sub() {
      if (this.unsub) this.unsub();
      this.unsub = subscribeUpscaleJob(this.jobId, (job) => (this.job = job));
    },
  },
  render(h) {
    if (!this.job || (this.job.status !== "done" && this.job.status !== "failed")) return null;
    return h(UpscaleJobCard, { props: { job: this.job }, class: "mt-2" });
  },
};

/** /generate: Website-Generierung (generateArtworkV9) für interne Konten, optional Upscale-Job */
export default {
  name: "GenerateView",
  components: { UpscaleProgress, UpscaleJobResult },
  data() {
    return {
      initializing: true,
      authUser: null,
      authLoading: false,
      authError: "",
      loginEmail: "",
      loginPassword: "",
      styles: [],
      themes: [],
      metaError: "",
      styleId: "rustic",
      version: null,
      orientation: "portrait",
      uploads: [],
      dragOver: false,
      generating: false,
      genError: "",
      startedAt: 0,
      nowTick: 0,
      tick: null,
      results: [],
      imageDialog: false,
      activeImg: "",
      exampleFallback: {},
    };
  },
  computed: {
    currentStyle() {
      return this.styles.find((s) => s.id === this.styleId) || null;
    },
    currentVersionMeta() {
      if (!this.currentStyle) return null;
      return this.currentStyle.perVersion.find((v) => v.version === this.version) || null;
    },
    readyUploads() {
      return this.uploads.filter((u) => u.path && !u.uploading && !u.error);
    },
    canGenerate() {
      return !!this.currentStyle && !!this.version && this.readyUploads.length >= 1 && this.readyUploads.length === this.uploads.length && !this.generating;
    },
    elapsed() {
      const s = Math.max(0, Math.round((this.nowTick - this.startedAt) / 1000));
      return `${s} s`;
    },
  },
  mounted() {
    this.initAuth();
    this.loadMeta();
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      if (Array.isArray(saved)) this.results = saved.map((r) => ({ ...r, up: { ...(r.up || {}), starting: false } }));
    } catch (e) {
      /* leer */
    }
  },
  beforeDestroy() {
    clearInterval(this.tick);
  },
  watch: {
    results: {
      deep: true,
      handler(v) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(v.slice(0, 40)));
        } catch (e) {
          /* voll */
        }
      },
    },
  },
  methods: {
    // ---------- Auth (ohmyart-ai) ----------
    initAuth() {
      const { auth } = getOhmyartApp();
      onAuthStateChanged(auth, (user) => {
        this.authUser = user;
        this.initializing = false;
      });
    },
    async handleLogin() {
      if (!this.loginEmail || !this.loginPassword) return;
      this.authLoading = true;
      this.authError = "";
      try {
        await signInWithEmailAndPassword(getOhmyartApp().auth, this.loginEmail, this.loginPassword);
      } catch (e) {
        this.authError = "Login fehlgeschlagen.";
      } finally {
        this.authLoading = false;
      }
    },
    async handleLogout() {
      await signOut(getOhmyartApp().auth);
    },
    // ---------- Stil-Metadaten (über TaskDash-Admin-Proxy) ----------
    async loadMeta() {
      try {
        const call = httpsCallable(mainFunctions, "themePreviewAction", { timeout: 60000 });
        const { data } = await call({ action: "styleMeta", payload: {} });
        const all = (data && data.styles) || [];
        // Classic zuerst in Editor-Reihenfolge, dann der Rest alphabetisch
        const order = (id) => (CLASSIC.includes(id) ? CLASSIC.indexOf(id) : 100 + id.charCodeAt(0));
        this.styles = all.slice().sort((a, b) => order(a.id) - order(b.id));
        this.themes = (data && data.themes) || [];
        if (!this.styles.find((s) => s.id === this.styleId)) this.styleId = this.styles[0] ? this.styles[0].id : null;
        this.version = this.currentStyle ? this.currentStyle.versions : null;
      } catch (e) {
        this.metaError = "Stil-Daten nicht erreichbar: " + (e.message || e);
      }
    },
    selectStyle(id) {
      this.styleId = id;
      const s = this.currentStyle;
      // gewählte Version behalten, sonst höchste des Stils (wie Editor-Präferenz)
      this.version = s ? Math.min(this.version || s.versions, s.versions) : null;
    },
    versionFor(s) {
      return s.id === this.styleId && this.version ? this.version : s.versions;
    },
    /**
     * Beispielbild je Stil: irgendein vorhandenes Motiv aus dem Storage-Verzeichnis (getStyleMeta liefert
     * die Liste; „all" bevorzugt). Reihenfolge: Version in gewählter Ausrichtung → ohne Version →
     * dieselben in der anderen Ausrichtung. Fehlt alles, bleibt nur ein Platzhalter.
     */
    exampleCandidates(s, orientation, version) {
      const transparent = s.transparentVersions.includes(version);
      const ex = s.examples || {};
      const list = [];
      const ratios = orientation === "landscape" ? ["3:2", "2:3"] : ["2:3", "3:2"];
      for (const ratio of ratios) {
        const g = ex[ratio] || {};
        const ori = ratio === "3:2" ? "landscape" : "portrait";
        const vThemes = g[`v${version}`] || [];
        if (vThemes.length) list.push(styleExampleUrl(s.id, ori, version, transparent, vThemes[0]));
        const legacy = g.legacy || [];
        if (legacy.length) list.push(styleExampleUrl(s.id, ori, null, false, legacy[0]));
      }
      if (!list.length) list.push(styleExampleUrl(s.id, orientation, null, false, "all"));
      return list;
    },
    exampleUrl(s, orientation, version) {
      const key = `${s.id}|${orientation}|${version}`;
      const list = this.exampleCandidates(s, orientation, version);
      return list[Math.min(this.exampleFallback[key] || 0, list.length - 1)];
    },
    onExampleError(ev, s) {
      const v = this.versionFor(s);
      const key = `${s.id}|${this.orientation}|${v}`;
      const next = (this.exampleFallback[key] || 0) + 1;
      if (next < this.exampleCandidates(s, this.orientation, v).length) this.$set(this.exampleFallback, key, next);
    },
    // ---------- Uploads ----------
    onPick(e) {
      this.addFiles([...(e.target.files || [])]);
      e.target.value = "";
    },
    onDrop(e) {
      this.dragOver = false;
      this.addFiles([...(e.dataTransfer.files || [])].filter((f) => f.type.startsWith("image/")));
    },
    addFiles(files) {
      const room = 4 - this.uploads.length;
      files.slice(0, Math.max(0, room)).forEach((file) => this.uploadFile(file));
    },
    async uploadFile(file) {
      const id = uuid();
      const entry = { id, name: file.name, preview: URL.createObjectURL(file), uploading: true, error: "", path: null };
      this.uploads.push(entry);
      try {
        const { auth, storage } = getOhmyartApp();
        const uid = auth.currentUser.uid;
        const d = new Date();
        const ym = `${d.getFullYear()}_${String(d.getMonth() + 1).padStart(2, "0")}`;
        const ext = (file.name.match(/\.[a-z0-9]+$/i) || [".jpg"])[0].toLowerCase();
        const path = `user_uploads/${ym}/${uid}/${id}${ext}`;
        await uploadBytes(storageRef(storage, path), file, { contentType: file.type || "image/jpeg" });
        const u = this.uploads.find((x) => x.id === id);
        if (u) {
          u.path = path;
          u.uploading = false;
        }
      } catch (e) {
        const u = this.uploads.find((x) => x.id === id);
        if (u) {
          u.uploading = false;
          u.error = e.message || "Upload fehlgeschlagen";
        }
        EventBus.$emit(events.SNACKBAR, { message: `Upload fehlgeschlagen: ${e.message || e}`, type: "error" });
      }
    },
    removeUpload(id) {
      this.uploads = this.uploads.filter((u) => u.id !== id);
    },
    // ---------- Generieren ----------
    async generate() {
      if (!this.canGenerate) return;
      this.generating = true;
      this.genError = "";
      this.startedAt = Date.now();
      this.nowTick = Date.now();
      this.tick = setInterval(() => (this.nowTick = Date.now()), 1000);
      const paths = this.readyUploads.map((u) => u.path);
      const inputs = this.readyUploads.map((u) => u.preview);
      const styleId = this.styleId;
      const version = this.version;
      const orientation = this.orientation;
      const meta = this.currentVersionMeta;
      try {
        const { functions } = getOhmyartApp();
        const call = httpsCallable(functions, "generateArtworkV9", { timeout: 540000 });
        const payload = {
          styleId,
          version,
          aspectRatio: orientation === "landscape" ? "3:2" : "2:3",
          requestId: uuid().replace(/-/g, ""),
          ...(paths.length >= 2 ? { storagePaths: paths, storagePath: paths[0] } : { storagePath: paths[0] }),
        };
        const { data } = await call(payload);
        if (!data || !data.url) throw new Error("Keine Bild-URL in der Antwort");
        this.results.unshift({
          id: uuid(),
          at: Date.now(),
          ms: Date.now() - this.startedAt,
          styleId,
          version,
          orientation,
          inputs,
          combined: paths.length >= 2,
          url: data.url,
          opaqueUrl: data.opaqueUrl || null,
          docId: data.docId || null,
          requestId: data.requestId || payload.requestId,
          cutout: data.cutout || "none",
          meta,
          view: "final",
          bg: data.cutout === "done" ? "dark" : "light",
          up: {
            engine: "default",
            resolution: "4K",
            detailPass: !!(meta && meta.print && meta.print.detailPass && meta.print.detailPass.enabled),
            cutout: !!(meta && meta.transparent),
            jobId: null,
            starting: false,
            error: "",
          },
        });
      } catch (e) {
        this.genError = e.message || String(e);
      } finally {
        clearInterval(this.tick);
        this.generating = false;
      }
    },
    resultUrl(r) {
      return r.view === "opaque" && r.opaqueUrl ? r.opaqueUrl : r.url;
    },
    removeResult(id) {
      this.results = this.results.filter((r) => r.id !== id);
    },
    // ---------- Upscale ----------
    engineItems(r) {
      const std = r.meta && r.meta.print && r.meta.print.upscale && r.meta.print.upscale.engine;
      return [
        { text: `Stil-Standard (${std || "–"})`, value: "default" },
        { text: "Gemini (gemini-nano-2)", value: "gemini-nano-2" },
        { text: "Sunburst (gpt-image-2.5-sunburst)", value: "gpt-image-2.5-sunburst" },
      ];
    },
    async startUpscale(r) {
      r.up.starting = true;
      r.up.error = "";
      try {
        const call = httpsCallable(mainFunctions, "startLabUpscale", { timeout: 60000 });
        const { data } = await call({
          print: r.meta.print,
          transparent: !!r.meta.transparent,
          styleId: r.styleId,
          version: r.version,
          artworkUrl: r.url,
          opaqueUrl: r.opaqueUrl || r.url,
          photos: [],
          resolution: r.up.resolution,
          engine: r.up.engine === "default" ? null : r.up.engine,
          detailPass: r.up.detailPass,
          cutout: r.meta.transparent ? r.up.cutout : false,
          label: `TaskDash ${r.styleId} v${r.version}`,
        });
        r.up.jobId = data.jobId;
      } catch (e) {
        r.up.error = e.message || String(e);
      } finally {
        r.up.starting = false;
      }
    },
    // ---------- Helfer ----------
    copy(text) {
      navigator.clipboard && navigator.clipboard.writeText(text);
      EventBus.$emit(events.SNACKBAR, { message: "URL kopiert", type: "success" });
    },
    openImage(u) {
      if (!u) return;
      this.activeImg = u;
      this.imageDialog = true;
    },
    fmtMs(ms) {
      return ms ? `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)} s` : "–";
    },
    fmtTime(ts) {
      return new Date(ts).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    },
  },
};
</script>

<style lang="scss" scoped>
$accent: #7b5cd6;
$shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07);

.gv {
  padding: 16px 20px 40px;
  background: #f4f5f8;
  min-height: 100vh;
  font-size: 13px;

  &__login {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 70vh;
  }
  &__loginCard {
    width: 380px;
    padding: 28px;
    border-radius: 16px;
    background: #fff;
    box-shadow: $shadow;
  }
  &__loginTitle {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 800;
    margin-bottom: 4px;
  }
  &__muted {
    color: rgba(0, 0, 0, 0.5);
    font-size: 12px;
  }
  &__error {
    color: #c62828;
    font-size: 12px;
    font-weight: 600;
  }
  &__mono {
    font-family: ui-monospace, Menlo, monospace;
    font-size: 11px;
  }
  &__top {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }
  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 18px;
  }
  &__layout {
    display: grid;
    grid-template-columns: 400px 1fr;
    gap: 14px;
    align-items: start;

    @media (max-width: 1100px) {
      grid-template-columns: 1fr;
    }
  }
  &__panel {
    display: grid;
    gap: 10px;
    position: sticky;
    top: 16px;
  }
  &__card {
    background: #fff;
    border-radius: 14px;
    padding: 12px 14px;
    box-shadow: $shadow;
  }
  &__cardHead {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }
  &__step {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #282828;
    color: #fff;
    font-size: 11px;
    font-weight: 800;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  &__drop {
    border: 2px dashed rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    min-height: 120px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 10px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;

    &--over {
      border-color: $accent;
      background: #f3f0fc;
    }
  }
  &__thumbs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
  }
  &__thumb {
    position: relative;
    width: 84px;
    height: 84px;
    border-radius: 10px;
    overflow: hidden;
    background: #eee;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    &--busy img {
      opacity: 0.4;
    }
    &--err {
      outline: 2px solid #c62828;
    }
  }
  &__thumbSpin {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  &__thumbX {
    position: absolute;
    top: 3px;
    right: 3px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
  }
  &__thumbAdd {
    width: 84px;
    height: 84px;
    border-radius: 10px;
    border: 2px dashed rgba(0, 0, 0, 0.15);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  &__hint {
    margin-top: 6px;
    font-size: 11.5px;
    color: #6a4fc4;
    font-weight: 600;
  }
  &__styles {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }
  &__style {
    position: relative;
    border-radius: 10px;
    overflow: hidden;
    background: #f1f2f6;
    aspect-ratio: 2 / 3;
    cursor: pointer;
    outline: 2px solid transparent;
    transition: outline-color 0.12s, transform 0.12s;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    &:hover {
      transform: translateY(-1px);
    }
    &--on {
      outline-color: $accent;
    }
  }
  &__styleName {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 4px 6px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    text-align: left;
    display: flex;
    justify-content: space-between;
  }
  &__styleV {
    opacity: 0.85;
  }

  &__rowOpts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  &__segmented {
    display: inline-flex;
    gap: 2px;
    padding: 2px;
    border-radius: 999px;
    background: #e6e8ee;

    &--mini .gv__segBtn {
      padding: 1px 8px;
      font-size: 10.5px;
    }
  }
  &__segBtn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.6);
    cursor: pointer;

    &--on {
      background: #fff;
      color: #282828;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }
  }
  &__segTag {
    font-size: 9.5px;
    padding: 0 5px;
    border-radius: 999px;
    background: #edeafa;
    color: #6a4fc4;
  }
  &__metaLine {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 8px;
  }
  &__chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    background: #f1f2f6;
    font-size: 11.5px;
    white-space: nowrap;

    &--bad {
      background: #fbe4e4;
      color: #c62828;
    }
  }
  &__go {
    margin-top: 2px;
  }
  &__results {
    display: grid;
    gap: 10px;
  }
  &__empty {
    background: #fff;
    border-radius: 14px;
    padding: 50px;
    text-align: center;
    box-shadow: $shadow;
  }
  &__result {
    background: #fff;
    border-radius: 14px;
    padding: 12px 14px;
    box-shadow: $shadow;
  }
  &__resultHead {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
  }
  &__iconBtn {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
      background: #f0f0f4;
    }
  }
  &__resultBody {
    display: grid;
    grid-template-columns: 120px 300px 1fr;
    gap: 12px;
    align-items: start;

    @media (max-width: 1300px) {
      grid-template-columns: 100px 240px 1fr;
    }
  }
  &__inputs {
    display: grid;
    gap: 6px;

    img {
      width: 100%;
      border-radius: 8px;
      cursor: zoom-in;
      background: #f1f2f6;
    }
  }
  &__out {
    border-radius: 10px;
    overflow: hidden;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: zoom-in;

    img {
      width: 100%;
      display: block;
    }
    &--dark {
      background: #1f2937;
    }
    &--light {
      background: #f1f2f6;
    }
  }
  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }
  &__linkBtn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 999px;
    background: #f1f2f6;
    color: #282828 !important;
    text-decoration: none;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      background: #edeafa;
    }
  }
  &__upscale {
    flex-basis: 100%;
    margin-top: 8px;
    padding: 10px 12px;
    border-radius: 12px;
    background: #f6f7f9;
  }
  &__upHead {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }
  &__upOpts {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
  }
  &__select {
    max-width: 280px;
    flex: 1;

    &--s {
      max-width: 110px;
      flex: 0 0 110px;
    }
  }
  &__switch {
    margin: 0;
    padding: 0;
  }
  &__upRow {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }
  &__imgWrap {
    display: flex;
    justify-content: center;
    cursor: zoom-out;

    img {
      max-width: 92vw;
      max-height: 90vh;
      border-radius: 12px;
      background: #fff;
    }
  }
}
</style>
